import { useState, useEffect, useRef } from 'react';
import { useBranch } from '../contexts/BranchContext';
import { useAuth } from '../App';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  ChatCircleDots, 
  PaperPlaneTilt, 
  Robot, 
  Lightning, 
  ChartLine,
  FileText,
  Sparkle
} from '@phosphor-icons/react';
import api from '../utils/api';

// ModeCard component - moved outside to fix React lint warning
const ModeCard = ({ mode, config, activeMode, setActiveMode, setMessages }) => {
  const Icon = config.icon;
  const isActive = activeMode === mode;
  
  return (
    <button
      onClick={() => {
        setActiveMode(mode);
        setMessages([]);
      }}
      className={`p-4 rounded-lg border-2 transition-all text-left w-full ${
        isActive 
          ? `border-${config.color}-500 bg-${config.color}-50` 
          : 'border-gray-200 hover:border-gray-300'
      }`}
    >
      <div className="flex items-center gap-3">
        <Icon size={24} weight={isActive ? 'fill' : 'regular'} />
        <div>
          <div className="font-semibold">{config.title}</div>
          <div className="text-xs text-gray-600">{config.placeholder.substring(0, 40)}...</div>
        </div>
      </div>
    </button>
  );
};

const AIAssistant = () => {
  const { activeBranch } = useBranch();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeMode, setActiveMode] = useState('chat');
  const [sessionId] = useState(`session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const messagesEndRef = useRef(null);
  const eventSourceRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, []);

  const getModeConfig = (mode) => {
    const configs = {
      chat: {
        title: 'AI Chatbot',
        icon: ChatCircleDots,
        color: 'blue',
        placeholder: 'Ask me anything about your business...',
        systemPrompt: 'You are a helpful AI assistant for a therapy center CRM. Help users with their queries about customers, sales, inventory, and business operations.'
      },
      content: {
        title: 'Content Generation',
        icon: FileText,
        color: 'purple',
        placeholder: 'What content do you need? (e.g., product description, email template)',
        systemPrompt: 'You are a professional content writer. Generate high-quality, business-appropriate content based on user requests.'
      },
      analysis: {
        title: 'Data Analysis',
        icon: ChartLine,
        color: 'green',
        placeholder: 'Ask for insights about your CRM data...',
        systemPrompt: 'You are a data analyst. Provide insights, patterns, and recommendations based on business data and metrics.'
      },
      automated: {
        title: 'Quick Responses',
        icon: Lightning,
        color: 'orange',
        placeholder: 'Generate quick response for customer...',
        systemPrompt: 'You are a customer service expert. Generate quick, professional, and helpful responses for customer queries.'
      }
    };
    return configs[mode] || configs.chat;
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMsg = {
      role: 'user',
      content: inputMessage.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMsg]);
    setInputMessage('');
    setIsLoading(true);

    const aiMsg = {
      role: 'assistant',
      content: '',
      timestamp: new Date().toISOString(),
      isStreaming: true
    };
    setMessages(prev => [...prev, aiMsg]);

    try {
      const modeConfig = getModeConfig(activeMode);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          message: userMsg.content,
          session_id: sessionId,
          mode: activeMode,
          system_prompt: modeConfig.systemPrompt,
          branch_id: activeBranch.branch_id,
          user_role: user?.role
        })
      });

      if (!response.ok) {
        throw new Error('Failed to get AI response');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') {
              setMessages(prev => 
                prev.map((msg, idx) => 
                  idx === prev.length - 1 ? { ...msg, isStreaming: false } : msg
                )
              );
            } else {
              try {
                const parsed = JSON.parse(data);
                if (parsed.error) {
                  // Handle billing errors
                  if (parsed.billing_error) {
                    toast.error(parsed.error, { duration: 5000 });
                  } else {
                    toast.error(`Error: ${parsed.error}`);
                  }
                  setMessages(prev => prev.slice(0, -1));
                  setIsLoading(false);
                  return;
                }
                if (parsed.content) {
                  setMessages(prev => 
                    prev.map((msg, idx) => 
                      idx === prev.length - 1 
                        ? { ...msg, content: msg.content + parsed.content }
                        : msg
                    )
                  );
                }
              } catch (e) {
                console.error('Parse error:', e);
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('AI Chat Error:', error);
      toast.error('Failed to get AI response');
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setIsLoading(false);
    }
  };

  const clearChat = () => {
    setMessages([]);
    toast.success('Chat cleared');
  };

  const currentConfig = getModeConfig(activeMode);
  const CurrentIcon = currentConfig.icon;

  return (
    <div className="p-4 space-y-4 h-screen flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-gradient-to-br from-purple-500 to-blue-500 rounded-lg">
            <Sparkle size={28} weight="fill" className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#2C4C3B]">AI Assistant</h1>
            <p className="text-sm text-gray-600">Powered by Claude Sonnet 5</p>
          </div>
        </div>
        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
          <Robot size={16} className="mr-1" weight="fill" />
          Active
        </Badge>
      </div>

      {/* Mode Selection */}
      <div className="grid grid-cols-4 gap-3">
        <ModeCard mode="chat" config={getModeConfig('chat')} activeMode={activeMode} setActiveMode={setActiveMode} setMessages={setMessages} />
        <ModeCard mode="content" config={getModeConfig('content')} activeMode={activeMode} setActiveMode={setActiveMode} setMessages={setMessages} />
        <ModeCard mode="analysis" config={getModeConfig('analysis')} activeMode={activeMode} setActiveMode={setActiveMode} setMessages={setMessages} />
        <ModeCard mode="automated" config={getModeConfig('automated')} activeMode={activeMode} setActiveMode={setActiveMode} setMessages={setMessages} />
      </div>

      {/* Chat Container */}
      <Card className="flex-1 flex flex-col overflow-hidden">
        {/* Chat Header */}
        <div className="p-4 border-b bg-gray-50 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CurrentIcon size={20} />
            <span className="font-semibold">{currentConfig.title}</span>
          </div>
          {messages.length > 0 && (
            <Button size="sm" variant="outline" onClick={clearChat}>
              Clear Chat
            </Button>
          )}
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 ? (
            <div className="h-full flex items-center justify-center">
              <div className="text-center text-gray-500">
                <CurrentIcon size={48} className="mx-auto mb-3 opacity-30" />
                <p className="font-medium">Start a conversation</p>
                <p className="text-sm">{currentConfig.placeholder}</p>
              </div>
            </div>
          ) : (
            messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] p-3 rounded-lg ${
                    msg.role === 'user'
                      ? 'bg-[#2C4C3B] text-white'
                      : 'bg-gray-100 text-gray-900'
                  }`}
                >
                  <div className="text-sm whitespace-pre-wrap">{msg.content}</div>
                  {msg.isStreaming && (
                    <div className="flex items-center gap-1 mt-2">
                      <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                      <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                      <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                    </div>
                  )}
                  <div className="text-xs opacity-70 mt-1">
                    {new Date(msg.timestamp).toLocaleTimeString()}
                  </div>
                </div>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-4 border-t bg-white">
          <div className="flex gap-2">
            <Input
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
              placeholder={currentConfig.placeholder}
              disabled={isLoading}
              className="flex-1"
            />
            <Button
              onClick={sendMessage}
              disabled={isLoading || !inputMessage.trim()}
              className="bg-[#2C4C3B] hover:bg-[#1e3329]"
            >
              <PaperPlaneTilt size={20} weight="fill" />
            </Button>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            AI can make mistakes. Verify important information.
          </p>
        </div>
      </Card>
    </div>
  );
};

export default AIAssistant;
